import { Capacitor } from '@capacitor/core';
import 'cordova-plugin-purchase';
import { FAMILY_PRODUCT_ID, IOS_PREMIUM_MONTHLY_PRODUCT_ID, isCommerceEnabled } from '../config/commerce';

export { FAMILY_PRODUCT_ID, IOS_PREMIUM_MONTHLY_PRODUCT_ID };

export type PurchaseOffer = {
  available: boolean;
  owned: boolean;
  /** True tek kada je StoreKit učitao lokalni receipt i vlasništvo je pouzdano provereno. */
  ownershipChecked?: boolean;
  price?: string;
  trialDays?: number;
  reason?: string;
  retryable?: boolean;
};

export type PurchaseResult = {
  state: 'verified' | 'pending' | 'cancelled' | 'unavailable' | 'failed';
  message?: string;
};

export type RestoreResult = {
  owned: boolean;
  ownershipChecked?: boolean;
  message?: string;
};

export interface PurchaseGateway {
  initialize(): Promise<PurchaseOffer>;
  purchase(): Promise<PurchaseResult>;
  restore(): Promise<RestoreResult>;
  subscribeOwnership?(listener: (owned: boolean) => void): () => void;
}

type PricingPhaseLike = {
  price?: string;
  billingPeriod?: string;
  paymentMode?: string;
};

type StoreProductLike = {
  pricing?: { price?: string };
  getOffer(): { pricingPhases?: PricingPhaseLike[] } | undefined;
};

function trialDaysFromBillingPeriod(period?: string): number | undefined {
  if (!period) return undefined;
  const days = /^P(\d+)D$/.exec(period)?.[1];
  if (days) return Number(days);
  const weeks = /^P(\d+)W$/.exec(period)?.[1];
  if (weeks) return Number(weeks) * 7;
  return undefined;
}

export function friendlyStoreMessage(message?: string): string {
  const normalized = message?.toLocaleLowerCase('en') ?? '';
  if (normalized.includes('product not found') || normalized.includes('#400')) {
    return 'Apple ponuda još nije učitana. Proverite internet vezu i ponovite proveru za nekoliko minuta.';
  }
  return message || 'Apple prodavnica trenutno nije dostupna. Ponovite proveru.';
}

export function purchaseOfferFromProduct(
  product: StoreProductLike | undefined,
  owned: boolean,
  unavailableReason?: string,
  ownershipChecked = true
): PurchaseOffer {
  const offer = product?.getOffer();
  if (!offer) {
    return {
      available: false,
      owned,
      ownershipChecked,
      reason: friendlyStoreMessage(unavailableReason || 'Slovolov Premium ponuda još nije učitana iz App Store-a.'),
      retryable: true
    };
  }

  const phases = offer.pricingPhases ?? [];
  const freeTrial = phases.find((phase) => phase.paymentMode === 'FreeTrial');
  const paidPhase = [...phases].reverse().find((phase) => phase.paymentMode !== 'FreeTrial');
  const trialDays = trialDaysFromBillingPeriod(freeTrial?.billingPeriod);
  const price = paidPhase?.price ?? product?.pricing?.price;

  if (!price) {
    return {
      available: false,
      owned,
      ownershipChecked,
      reason: 'Apple ponuda još nema potvrđenu cenu. Ponovite proveru ponude.',
      retryable: true
    };
  }

  return {
    available: true,
    owned,
    ownershipChecked,
    price,
    ...(trialDays ? { trialDays } : {})
  };
}

export function createPurchaseManager(
  gateway: PurchaseGateway,
  setSubscriptionAccess: (isUnlocked: boolean) => void
) {
  return {
    initialize: async () => {
      const offer = await gateway.initialize();
      // Negativno vlasništvo sme da zaključa Premium samo nakon pouzdano
      // učitanog StoreKit receipt-a. Tako prekid mreže ne zaključava aktivnog
      // roditelja, ali istekla ili otkazana pretplata više ne ostaje trajno
      // otvorena na uređaju.
      if (offer.ownershipChecked) setSubscriptionAccess(offer.owned);
      return offer;
    },
    purchase: async () => {
      const result = await gateway.purchase();
      if (result.state === 'verified') setSubscriptionAccess(true);
      return result;
    },
    restore: async () => {
      const result = await gateway.restore();
      if (result.owned || result.ownershipChecked) setSubscriptionAccess(result.owned);
      return result;
    },
    subscribeOwnership: () => gateway.subscribeOwnership?.((owned) => {
      // Native gateway šalje ovaj događaj tek pošto su receipt-i spremni.
      // Zato je i `false` pouzdana potvrda da je pravo isteklo.
      setSubscriptionAccess(owned);
    }) ?? (() => undefined)
  };
}

function webGateway(): PurchaseGateway {
  const reason = 'Slovolov Premium je trenutno dostupan samo u iOS aplikaciji.';
  return {
    initialize: async () => ({ available: false, owned: false, ownershipChecked: false, reason }),
    purchase: async () => ({ state: 'unavailable', message: reason }),
    restore: async () => ({ owned: false, ownershipChecked: false, message: reason })
  };
}

function hasNativePurchasePlugin(): boolean {
  return typeof CdvPurchase !== 'undefined' && Boolean(CdvPurchase?.store);
}

/**
 * Capacitor može da prikaže lokalni web ekran pre nego što Cordova završi
 * ubacivanje prodavničkog mosta. Ne smemo tada trajno izabrati web gateway.
 */
async function waitForNativePurchasePlugin(timeoutMs: number): Promise<boolean> {
  if (hasNativePurchasePlugin()) return true;

  return new Promise((resolve) => {
    let settled = false;
    const finish = (available: boolean) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      window.clearInterval(poll);
      document.removeEventListener('deviceready', check);
      resolve(available);
    };
    const check = () => {
      if (hasNativePurchasePlugin()) finish(true);
    };
    const poll = window.setInterval(check, 50);
    const timeout = window.setTimeout(() => finish(hasNativePurchasePlugin()), timeoutMs);
    document.addEventListener('deviceready', check);
    check();
  });
}

export function createNativePurchaseGatewayWhenReady(pluginTimeoutMs = 10_000): PurchaseGateway {
  let delegate: PurchaseGateway | null = null;
  let resolving: Promise<PurchaseGateway | null> | null = null;
  const storeName = Capacitor.getPlatform() === 'android' ? 'Google Play' : 'Apple';
  const unavailableReason = `${storeName} kupovina se još povezuje sa aplikacijom. Sačekajte trenutak i ponovite proveru.`;

  const resolveDelegate = async (): Promise<PurchaseGateway | null> => {
    if (delegate) return delegate;
    resolving ??= waitForNativePurchasePlugin(pluginTimeoutMs).then((available) => {
      resolving = null;
      if (!available) return null;
      delegate ??= nativeGateway();
      return delegate;
    });
    return resolving;
  };

  return {
    initialize: async () => {
      const gateway = await resolveDelegate();
      if (!gateway) {
        return {
          available: false,
          owned: false,
          ownershipChecked: false,
          reason: unavailableReason,
          retryable: true
        };
      }
      return gateway.initialize();
    },
    purchase: async () => {
      const gateway = await resolveDelegate();
      return gateway
        ? gateway.purchase()
        : { state: 'unavailable', message: unavailableReason };
    },
    restore: async () => {
      const gateway = await resolveDelegate();
      return gateway
        ? gateway.restore()
        : { owned: false, ownershipChecked: false, message: unavailableReason };
    },
    subscribeOwnership: (listener) => {
      let cancelled = false;
      let unsubscribe: () => void = () => undefined;
      void resolveDelegate().then((gateway) => {
        if (!gateway || cancelled) return;
        unsubscribe = gateway.subscribeOwnership?.(listener) ?? (() => undefined);
      });
      return () => {
        cancelled = true;
        unsubscribe();
      };
    }
  };
}

function nativeGateway(): PurchaseGateway {
  const isIos = Capacitor.getPlatform() === 'ios';
  const platform = isIos ? CdvPurchase.Platform.APPLE_APPSTORE : CdvPurchase.Platform.GOOGLE_PLAY;
  const productId = isIos ? IOS_PREMIUM_MONTHLY_PRODUCT_ID : FAMILY_PRODUCT_ID;
  const productType = isIos ? CdvPurchase.ProductType.PAID_SUBSCRIPTION : CdvPurchase.ProductType.NON_CONSUMABLE;
  const storeName = isIos ? 'App Store' : 'Google Play';
  const store = CdvPurchase.store;
  let initialized = false;
  let initializationPromise: Promise<CdvPurchase.IError[]> | null = null;
  let receiptsReady = false;
  let resolveReceiptsReady: (() => void) | undefined;
  const receiptsReadyPromise = new Promise<void>((resolve) => {
    resolveReceiptsReady = resolve;
  });
  const ownershipListeners = new Set<(owned: boolean) => void>();

  const product = () => store.get(productId, platform);
  const ownsProduct = () => store.owned({ id: productId, platform });
  const notifyOwnership = () => {
    const owned = ownsProduct();
    ownershipListeners.forEach((listener) => listener(owned));
  };
  store.when().receiptsReady(() => {
    receiptsReady = true;
    resolveReceiptsReady?.();
    notifyOwnership();
  });
  store.when().receiptUpdated(() => {
    if (receiptsReady) notifyOwnership();
  });
  const waitForReceiptsReady = async (timeoutMs = 10_000): Promise<boolean> => {
    if (receiptsReady) return true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<false>((resolve) => {
      timer = setTimeout(() => resolve(false), timeoutMs);
    });
    const ready = receiptsReadyPromise.then(() => true as const);
    const result = await Promise.race([ready, timeout]);
    if (timer) clearTimeout(timer);
    return result;
  };
  const forceStoreUpdate = async () => {
    const previousMinimum = store.minTimeBetweenUpdates;
    store.minTimeBetweenUpdates = 0;
    try {
      await store.update();
    } finally {
      store.minTimeBetweenUpdates = previousMinimum;
    }
  };
  const waitForOwnership = async (timeoutMs = 15_000): Promise<boolean> => {
    if (ownsProduct()) return true;
    return new Promise((resolve) => {
      let settled = false;
      const finish = (owned: boolean) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        store.off(onReceipt);
        resolve(owned);
      };
      const onReceipt = () => {
        if (ownsProduct()) finish(true);
      };
      const timer = window.setTimeout(() => finish(ownsProduct()), timeoutMs);
      store.when().receiptUpdated(onReceipt);
    });
  };

  async function initialize(): Promise<PurchaseOffer> {
    let failureMessage: string | undefined;
    if (!initialized) {
      if (!initializationPromise) {
        store.register({
          id: productId,
          type: productType,
          platform
        });
        store.when().approved((transaction) => transaction.finish());
        initializationPromise = store.initialize([platform]);
      }
      const errors = await initializationPromise;
      initialized = true;
      const ownershipChecked = await waitForReceiptsReady();
      const fatal = errors.find((error) => error.productId === productId || error.platform === platform);
      failureMessage = fatal?.message;
      if (fatal) {
        return purchaseOfferFromProduct(undefined, ownsProduct(), fatal.message, ownershipChecked);
      }
    } else {
      // Biblioteka inače preskače StoreKit pozive do deset minuta. Korisnički
      // retry mora zaista ponovo da proveri Apple ponudu odmah.
      await forceStoreUpdate();
    }

    const ownershipChecked = receiptsReady || await waitForReceiptsReady();
    return purchaseOfferFromProduct(product(), ownsProduct(), failureMessage ?? `${storeName} ponuda nije učitana.`, ownershipChecked);
  }

  return {
    initialize,
    purchase: async () => {
      const offerState = await initialize();
      if (offerState.owned) return { state: 'verified' };
      const offer = product()?.getOffer();
      if (!offer) return { state: 'unavailable', message: offerState.reason };

      const error = await store.order(offer);
      if (error) {
        const cancelled = error.code === CdvPurchase.ErrorCode.PAYMENT_CANCELLED;
        return { state: cancelled ? 'cancelled' : 'failed', message: error.message };
      }
      await forceStoreUpdate();
      return await waitForOwnership()
        ? { state: 'verified' }
        : { state: 'pending', message: 'Kupovina čeka potvrdu prodavnice.' };
    },
    restore: async () => {
      await initialize();
      // Vraćanje mora biti dostupno i kada App Store još nije učitao trenutnu ponudu.
      // Ranija aktivna pretplata može da postoji i tada.
      const error = await store.restorePurchases();
      if (error) return { owned: ownsProduct(), ownershipChecked: false, message: error.message };
      await forceStoreUpdate();
      return {
        owned: ownsProduct(),
        ownershipChecked: true,
        message: ownsProduct() ? undefined : 'Kupovina nije pronađena na ovom nalogu.'
      };
    },
    subscribeOwnership: (listener) => {
      ownershipListeners.add(listener);
      if (receiptsReady) queueMicrotask(() => listener(ownsProduct()));
      return () => ownershipListeners.delete(listener);
    }
  };
}

let defaultNativeGateway: PurchaseGateway | null = null;

export function createDefaultPurchaseGateway(): PurchaseGateway {
  if (!isCommerceEnabled() || !Capacitor.isNativePlatform() || !['ios', 'android'].includes(Capacitor.getPlatform())) return webGateway();
  defaultNativeGateway ??= createNativePurchaseGatewayWhenReady();
  return defaultNativeGateway;
}

export function isNativePurchasePlatform(): boolean {
  return Capacitor.isNativePlatform() && ['ios', 'android'].includes(Capacitor.getPlatform()) && isCommerceEnabled();
}
